import React, {useState, useEffect} from 'react';
import {useGet} from "../../hooks/useGet";
import Checkbox from "../../components/Checkbox";
import {DefaultButton, DefaultButtonLink} from "../../resources/styles";
import {useTheme} from "styled-components";
import {getFromHubNew, getResourceImageUrl, postToHubNew} from "../../actions/networkActions";
import {getTranslation} from "../../translations/translationUtils";
import CurrencyUtils from "../../utils/currencyUtils";
import {formatHourAndMinuteDuration, formatTranslatedDuration} from "../../utils/dateTimeUtils";
import {recordVisit} from "../../actions/metricsActions";
import ImageWithFallback from "../../components/ImageWithFallback";
import FullScreenLoader from '../../components/FullScreenLoader';
import { useSelector } from 'react-redux';
import PropTypes from "prop-types";
import {removeHtmlStyles} from "../../utils/stringUtils";

function OnboardExcursionBook(props, context) {
    const excursion = useGet(`excursions/${props.match.params.id}`);
    const conditionTexts = useGet("conditions/Excursion");
    const conditions = useGet("conditions/allowed/Excursion");
    const [guests, setGuests] = useState(null);
    const [tcCheckbox, setTcCheckbox] = useState(false);
    const [bookError, setBookError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const selectedCurrency = useSelector(state => state.currency.selectedCurrency || "£");
    const theme = useTheme();
    const excursionCurrency = selectedCurrency === "$" && theme.convertUsdExcursionToGbp ? "£" : selectedCurrency;
    const language = useSelector(state => state.i18nState.lang || "en");

    useEffect(() => {
        const fetchGuests = async () => {
            const guestsResponse = await getFromHubNew(`excursions/findApplicableGuests/${props.match.params.id}`);
            setGuests(guestsResponse.map(guest => ({...guest, selected: false})));
        }

        fetchGuests();
    }, [props.match.params.id]);
    

    const [bookingReference, setBookingReference] = useState("");

    if(excursion.data === null || conditionTexts.data === null || conditions.data === null || guests === null) {
        return (
            <FullScreenLoader />
        )
    }

    // TODO - handle the error case - look in excursion.error, guests.error,...
    const currentAvailability = excursion.data.currentAvailability === undefined ? 99 : excursion.data.currentAvailability;
    const nrSelected = guests.filter(guest => guest.selected).length;


    const book = async () => {
        if(!tcCheckbox) {
            setBookError(context.t('basket_tc_error'));
            return;
        }

        if(submitting) {
            return;
        }

        const selectedGuests = guests
            .filter(guest => guest.selected)
            .map(guest => ({
                    accountNumber: guest.accountNumber,
                    firstName: guest.firstName,
                    lastName: guest.lastName,
                })
            );

        if(selectedGuests.length === 0) {
            setBookError(context.t('pax_select_error'));
            return;
        }

        setSubmitting(true);
        const result = await postToHubNew(
            `excursions/book/${props.match.params.id}`,
            {
                excursionId: props.match.params.id,
                guests: selectedGuests,
                language
            });

        setSubmitting(false);
        if(!result || result.failed) {
            // TODO - remove the themed variable (showPmsExcursionFailureMessage) when the proper cause of failure is made available from resco. 
            if(theme.showPmsExcursionFailureMessage && result.cause){
                setBookError(result.cause==="BookedAlready" ? context.t('experience_already_booked') : context.t('hub_book_experience_error'));
            }else{
                setBookError(context.t('hub_book_experience_error'));
            }
        }
        else {
            recordVisit(`excursions/book/${props.match.params.id}`);
            setBookingReference(result.bookingReference);
        }

    }

    // conditions failed
    if (conditions.data !== null && conditions.data.messages?.length > 0) {
        return (
            <div>
                <div className="grid-x grid-padding-x">
                    <div className="cell small-12 medium-4 large-4">
                        <ImageWithFallback className="thumbnail"
                             src={getResourceImageUrl(excursion.data.id, {width: 400})}
                             alt="Excursion cover"
                        />
                    </div>
                </div>
                <div className="cell small-12 medium-8 large-8">
                    <div>
                        {
                            conditions.data.messages.map((message, index) =>
                                <h2 key={index}>{message}</h2>
                            )
                        }
                    </div>
                </div>
            </div>)
    }

    const calculateTotal = () => {
        return guests.reduce((sum, guest) => guest.selected ? sum + guest.price : sum,0);
    }

    // confirmation page
    if(bookingReference) {
        return (
            <div className="grid-x grid-padding-x">
                <div className="cell small-12 medium-4 large-4">
                    <ImageWithFallback className="thumbnail"
                         src={getResourceImageUrl(excursion.data.id, {width: 400})}
                         alt="Excursion cover"
                    />
                </div>
                <div className="cell small-12 medium-8 large-8">
                    <h4>
                        {context.t("your_experience")}
                        <span className="font-bold"> {getTranslation(excursion.data.name)} </span>
                        {context.t("is_successfully_booked")}
                    </h4>

                    <div className="my-4">
                        <div>
                            {context.t('date')}: {excursion.data.date}
                        </div>
                        {excursion.data.startTime &&
                        <div>
                            {context.t('time')}: {excursion.startTime}
                        </div>}

                        {excursion.data.duration &&
                            <div>
                                { context.t('duration')}: { theme.useHourAndMinuteDuration ? formatHourAndMinuteDuration(excursion.data.duration) :
                                    formatTranslatedDuration(excursion.data.duration, context.t('hour'), context.t('hours'))
                                }
                            </div>
                        }
                        <div>
                            {context.t('number_of_persons')}: {guests.filter(guest => guest.selected).length}
                        </div>

                        <div>
                            {context.t('total_price')}: {CurrencyUtils.formatCurrency(excursionCurrency, calculateTotal())}
                        </div>
                    </div>

                    <div className="my-4">
                        {context.t('book_reference_info')} <span className="font-bold">{bookingReference}</span>.
                    </div>

                    <div className="my-4">
                        {context.t('book_additional_info')}
                    </div>

                    <div className="my-4">
                        <DefaultButtonLink to="/my_onboard_bookings">
                            {context.t('my_bookings')}
                        </DefaultButtonLink>
                    </div>

                    <div className="my-4">
                        <div dangerouslySetInnerHTML={{__html: removeHtmlStyles(getTranslation(conditionTexts.data.additionalConditionText))}}/>
                    </div>
                </div>
            </div>
         )
    }


    const handleCheckboxChange = (accountNumber) => {
        const  updatedGuests = guests.map(guest => ({
            ...guest,
            selected: accountNumber === guest.accountNumber ? !guest.selected : guest.selected
        }));

        setGuests(updatedGuests);

        const nrSelectedUpdated = updatedGuests.filter(guest => guest.selected).length;
        if(nrSelectedUpdated > currentAvailability) {
            setBookError(`${context.t('overbooked')}. ${context.t('slots_remaining')}: ${currentAvailability}`);
        }
        else {
            if(bookError !== "") {
                setBookError("");
            }
        }
    }

    return (
        <div className="grid-x grid-padding-x">
            <div className="cell small-12 medium-4 large-4">
                <ImageWithFallback className="thumbnail"
                     src={getResourceImageUrl(excursion.data.id, {width: 400})}
                     alt="Excursion cover"
                />
            </div>

            <div className="cell small-12 medium-8 large-8">
                <div className="text-lg">
                    {context.t('participants_checkbox')}
                    <span className="font-bold"> {getTranslation(excursion.data.name)}</span> -
                    <span className="font-bold"> {excursion.data.date}</span>.
                </div>

                {currentAvailability < guests.length && (
                <p className="text-red my-4">{context.t('slots_remaining')}:</p>
                )}

                <div className="my-4">
                    {guests.map(guest =>
                        <div className="my-1" key={guest.accountNumber}>
                            <Checkbox
                                label={guest.accountName}
                                checked={guest.selected}
                                disabled={guest.disabled ? guest.disabled : false}
                                handleCheckboxChange={() => handleCheckboxChange(guest.accountNumber)}
                            />
                        </div>
                    )}
                </div>

                <div className="text-lg my-4">
                    {context.t('total_price')}: {CurrencyUtils.formatCurrency(excursionCurrency, calculateTotal())}
                </div>

                <div className="my-4">
                    <Checkbox
                        label={context.t('basket_accept_conditions')}
                        checked={tcCheckbox}
                        disabled={false}
                        handleCheckboxChange={() => setTcCheckbox(!tcCheckbox)}
                    />
                </div>

                {bookError &&
                <div className="text-red my-4">
                    {bookError}
                </div>}

                <DefaultButton disabled={submitting || currentAvailability < nrSelected || currentAvailability <= 0} onClick={book}>
                    {context.t('book_experience')}
                </DefaultButton>

                <div className="my-4" dangerouslySetInnerHTML={{__html: removeHtmlStyles(getTranslation(conditionTexts.data.conditionText))}}></div>

            </div>

        </div>)
}

OnboardExcursionBook.contextTypes = {
    t: PropTypes.func.isRequired,
};

export default OnboardExcursionBook;